/**
 * NotificationBell Component
 * Displays notification bell icon with unread count and dropdown panel with tabs
 * Desktop: Dropdown with category tabs
 * Mobile: Navigates to /notifications fullscreen page
 */

import React, { useState, useEffect, useRef } from 'react';
import { Bell } from 'lucide-react';
import {
  NOTIFICATION_TABS,
  filterNotificationsByTab,
} from '../utils/notificationAppearance';
import { haptic } from '../utils/haptics';
import Tabs from './ui/Tabs';
import EmptyState from './ui/EmptyState.jsx';
import NotificationRow, { NotificationRowSkeleton } from './notifications/NotificationRow';
import { useNavigate } from 'react-router-dom';
import { useNotificationContext } from '../contexts/NotificationContext';
import { navigateFromNotification } from '../utils/notificationRouting';

const NotificationBell = () => {
  const navigate = useNavigate();
  const { notifications, unreadCounts, isLoading, loadNotifications, markAsRead, markAllAsRead } = useNotificationContext();
  const [showPanel, setShowPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const panelRef = useRef(null);

  // Close panel on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        setShowPanel(false);
      }
    };

    if (showPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPanel]);

  const handleBellClick = async () => {
    // Mobile Browser OR Native App: Navigate to fullscreen notifications page
    // Desktop only: Show dropdown modal
    const isDesktopView = window.innerWidth >= 768 && !window.Capacitor?.isNativePlatform?.();
    
    if (!isDesktopView) {
      navigate('/notifications');
      return;
    }

    // Desktop: Toggle dropdown
    if (!showPanel) {
      await loadNotifications(20);
    }
    setShowPanel(!showPanel);
  };

  const handleNotificationClick = (notification) => {
    navigateFromNotification(navigate, notification, {
      markAsRead,
      onBeforeNavigate: () => setShowPanel(false),
    });
  };

  const handleMarkAllAsRead = async () => {
    haptic?.light?.();
    await markAllAsRead();
  };

  const getTabCount = (tabKey) => {
    switch (tabKey) {
      case 'all': return unreadCounts.nonChatTotal ?? unreadCounts.total;
      case 'connections': return unreadCounts.connections;
      case 'sessions': return unreadCounts.sessions;
      default: return 0;
    }
  };

  const filteredNotifications = filterNotificationsByTab(notifications, activeTab);
  const activeTabConfig = NOTIFICATION_TABS.find((t) => t.key === activeTab) || NOTIFICATION_TABS[0];
  const unreadTotal = unreadCounts.nonChatTotal ?? unreadCounts.total;

  return (
    <div className="relative" ref={panelRef}>
      {/* Glocke: derselbe Icon-Knopf wie die übrigen im Kopf, Zähler in Tinte.
          Gezählt werden nur ungelesene Benachrichtigungen ohne Chat. */}
      <button
        type="button"
        onClick={handleBellClick}
        className="relative flex h-11 w-11 items-center justify-center rounded-xl border border-border bg-surface/50 transition-colors duration-fast hover:bg-surface/80"
        aria-label={unreadTotal > 0 ? `Benachrichtigungen, ${unreadTotal} ungelesen` : 'Benachrichtigungen'}
      >
        <Bell className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        {unreadTotal > 0 && (
          <span
            className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 bg-foreground text-background ring-2 ring-surface text-2xs font-semibold tabular-nums rounded-full flex items-center justify-center"
            aria-hidden="true"
          >
            {unreadTotal > 9 ? '9+' : unreadTotal}
          </span>
        )}
      </button>

      {/* Notification Panel - Desktop Only (Mobile + Native App navigate to /notifications) */}
      {showPanel && window.innerWidth >= 768 && !window.Capacitor?.isNativePlatform?.() && (
        <div className="w-[calc(100vw-1rem)] md:w-96 bg-surface rounded-xl shadow-token-xl border border-border z-50 overflow-hidden
                        fixed right-2 top-14 md:absolute md:right-0 md:top-auto md:mt-2">
          {/* Header */}
          <div className="p-3 md:p-4 border-b border-border flex items-center justify-between bg-surface-raised">
            <h3 className="font-semibold text-foreground text-sm md:text-base">Benachrichtigungen</h3>
            {unreadTotal > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="text-sm text-primary hover:text-primary-hover font-medium flex items-center flex-shrink-0"
              >
                Alle gelesen
              </button>
            )}
          </div>

          {/* Tabs — dieselbe Leiste wie auf der Benachrichtigungsseite */}
          <Tabs
            variant="underline"
            fill
            ariaLabel="Benachrichtigungen filtern"
            value={activeTab}
            onChange={setActiveTab}
            className="bg-surface"
            items={NOTIFICATION_TABS.map((tab) => ({
              key: tab.key,
              label: tab.label,
              count: getTabCount(tab.key),
            }))}
          />

          {/* Content */}
          <div className="max-h-96 md:max-h-[32rem] overflow-y-auto">
            {isLoading && <NotificationRowSkeleton />}

            {!isLoading && filteredNotifications.length === 0 && (
              <EmptyState
                size="sm"
                icon={Bell}
                title="Keine Benachrichtigungen"
                text={activeTabConfig.emptyText}
              />
            )}

            {!isLoading && filteredNotifications.length > 0 && (
              <div>
                {filteredNotifications.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onSelect={handleNotificationClick}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
